import React from 'react';

import { Button, Flex, List, Tooltip } from 'antd';

import getSentimentBadge from '@/lib/helpers/getSentimentBadgeColor';
import getTimeInSeconds from '@/lib/helpers/getTimeInSeconds';
import prepareTimestampStr from '@/lib/helpers/prepareTimestampStr';

import styles from './styles.module.css';

type TProps = {
	item: {
		Name: string;
		Sentiment: string;
		Summary: string;
		Extract: string;
		Timestamps:
			| string[]
			| {
					timestamp: string;
					sentiment: string;
					extract: string;
			  }[];
	};
	videoSeekToTime: (seconds: number) => void;
	videoDuration: number;
};

export default function BrandEntityCollapsableItem({
	item,
	videoSeekToTime,
	videoDuration,
}: TProps) {
	const badge = getSentimentBadge(item?.Sentiment);

	return (
		<List.Item
			key={'brand-entity-list-item' + item?.Name}
			className="brand-entity-item"
		>
			<List.Item.Meta
				avatar={badge ? <span className={styles.badge}>{badge}</span> : null}
				title={
					<Flex
						gap={8}
						align="center"
						justify="flex-start"
						className={styles.flexWrap}
					>
						<span className={styles.itemName}>{item?.Name}</span>
						<span className={styles.itemSummary}>{item?.Summary}</span>
					</Flex>
				}
				description={
					<Flex
						gap={8}
						align="center"
						justify="flex-start"
						className={styles.flexWrap}
						style={{ marginTop: '0.5rem', color: '#000' }}
					>
						{item?.Timestamps?.map((timestampItem, idx) => {
							if (typeof timestampItem === 'string') {
								const seconds = getTimeInSeconds(timestampItem);

								const preparedTimestampStr = prepareTimestampStr(
									videoDuration,
									timestampItem,
								);

								if (seconds !== null) {
									return (
										<Button
											key={'brand-timestamp-' + timestampItem + idx}
											type="link"
											className={styles.timestampButton}
											onClick={() => videoSeekToTime(seconds)}
										>
											{preparedTimestampStr}
										</Button>
									);
								}
							}

							if (typeof timestampItem === 'object') {
								const seconds = getTimeInSeconds(timestampItem?.timestamp);

								const preparedTimestampStr = prepareTimestampStr(
									videoDuration,
									timestampItem?.timestamp,
								);

								const sentimentBadge = getSentimentBadge(
									timestampItem?.sentiment,
								);

								if (seconds !== null) {
									return (
										<React.Fragment
											key={
												'brand-timestamp-fragment' +
												timestampItem?.timestamp +
												idx
											}
										>
											{/* If extract is present, show it in a tooltip */}
											{typeof timestampItem?.extract === 'string' &&
											timestampItem?.extract?.length > 0 ? (
												<Flex
													style={{ width: '100%' }}
													gap={8}
													justify="flex-start"
													align="baseline"
												>
													<Tooltip
														title={timestampItem?.sentiment || ''}
														placement="top"
													>
														<Button
															key={
																'brand-timestamp-' +
																timestampItem?.timestamp +
																idx
															}
															type="link"
															className={styles.timestampButton}
															onClick={() => videoSeekToTime(seconds)}
														>
															<span>{sentimentBadge}</span>
															<span>{preparedTimestampStr}</span>
														</Button>
													</Tooltip>
													<span
														style={{
															fontWeight: 400,
															fontStyle: 'italic',
															color: '#000',
														}}
													>
														"{timestampItem?.extract}"
													</span>
												</Flex>
											) : (
												<Tooltip
													title={timestampItem?.sentiment || ''}
													placement="top"
												>
													<Button
														key={
															'brand-timestamp-' +
															timestampItem?.timestamp +
															idx
														}
														type="link"
														className={styles.timestampButton}
														onClick={() => videoSeekToTime(seconds)}
													>
														<span>{sentimentBadge}</span>
														<span>{preparedTimestampStr}</span>
													</Button>
												</Tooltip>
											)}
										</React.Fragment>
									);
								}
							}
							return;
						})}
					</Flex>
				}
			/>
		</List.Item>
	);
}
